import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import LanguageSwitch from '../components/LanguageSwitch';
import {
  clearQualificationResponses,
  initQualificationResponses,
  setQualificationOutcome,
  upsertQualificationAnswer,
} from '../utils/qualificationResponses';
import LegalModal from '../components/LegalModal';
import AppHeader from '../components/AppHeader';
import { useApplicantFlowPaths } from '../procedures/useApplicantFlowPaths';
import { useProcedureOptional } from '../procedures/ProcedureContext';
import { pickEn } from '../procedures/procedureContent';
import './Qualify.css';

/** Spain 2026 Regularization - legacy question tree (i18n keys). */
const SPAIN_STEP_MAP = {
  q1: { textKey: 'qualify.q1', onYes: 'q2', onNo: 'alternatives' },
  q2: { textKey: 'qualify.q2', onYes: 'q3', onNo: 'q5' },
  q3: { textKey: 'qualify.q3', onYes: 'alternatives', onNo: 'q4' },
  q4: { textKey: 'qualify.q4', onYes: 'alternatives', onNo: 'q5' },
  q5: { textKey: 'qualify.q5', onYes: 'alternatives', onNo: 'qualified' },
};

/**
 * Build a linear yes/no step map from a procedure's eligibility array.
 * Every "No" goes to alternatives; all "Yes" answers lead to qualified.
 */
function buildProcedureStepMap(eligibility) {
  if (!eligibility || eligibility.length === 0) return null;
  const items = eligibility.slice(0, 8);
  const map = {};
  items.forEach((item, i) => {
    const key = `q${i + 1}`;
    const nextKey = i + 1 < items.length ? `q${i + 2}` : 'qualified';
    map[key] = {
      label: pickEn(item.label),
      detail: pickEn(item.detail),
      onYes: nextKey,
      onNo: 'alternatives',
      itemId: item.id,
    };
  });
  return map;
}

export default function Qualify() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const paths = useApplicantFlowPaths();
  const proc = useProcedureOptional();

  const [step, setStep] = useState('q1');
  const [history, setHistory] = useState([]);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  // Determine which step map to use: procedure JSON eligibility or Spain legacy.
  const isProcedure = Boolean(proc?.slug);
  const procedureTitle = isProcedure ? pickEn(proc?.procedure?.title) : null;
  const eligibilityItems = isProcedure ? (proc?.bundle?.card?.eligibility || []) : [];

  const stepMap = useMemo(() => {
    if (!isProcedure) return SPAIN_STEP_MAP;
    return buildProcedureStepMap(eligibilityItems) || SPAIN_STEP_MAP;
  }, [isProcedure, eligibilityItems]);

  const question = stepMap[step];
  const isAlternatives = step === 'alternatives';
  const totalSteps = Object.keys(stepMap).length;
  const currentStepNum = Object.keys(stepMap).indexOf(step) + 1;

  useEffect(() => {
    initQualificationResponses();
  }, []);

  const goTo = (next, answer = null) => {
    if (question && answer) {
      upsertQualificationAnswer({
        questionId: question.itemId || step,
        questionText: question.label || t(question.textKey),
        answer,
      });
    }

    if (next === 'qualified') {
      setQualificationOutcome('qualified');
      navigate(paths.qualifyQualified);
      return;
    }

    if (next === 'alternatives') {
      setQualificationOutcome('alternatives');
    }

    setHistory((prev) => [...prev, step]);
    setStep(next);
  };

  const startOver = () => {
    clearQualificationResponses();
    initQualificationResponses();
    setStep('q1');
    setHistory([]);
  };

  return (
    <div className="qualify-page">
      <AppHeader onBrandClick={() => navigate(paths.landing)} rightSlot={<LanguageSwitch />} />

      <main className="qualify-main">
        <div className="qualify-row">
          <span className="qualify-eyebrow">{t('qualify.eyebrow')}</span>
          {!isAlternatives && (
            <span className="qualify-badge">
              {isProcedure
                ? `${currentStepNum} of ${totalSteps}`
                : t('qualify.badge')}
            </span>
          )}
        </div>

        {step === 'q1' && (
          <>
            <h1 className="qualify-title">
              {isProcedure
                ? <><span>Eligibility check</span></>
                : <Trans i18nKey="qualify.title" components={{ accent: <span /> }} />}
            </h1>
            <p className="qualify-sub">
              {isProcedure
                ? `Check if you meet the requirements for ${procedureTitle || 'this procedure'}.`
                : t('qualify.subtitle')}
            </p>
          </>
        )}

        <section className="qualify-card">
          {!isAlternatives && question ? (
            <>
              {/* Procedure mode: show requirement label + detail */}
              {isProcedure && question.label ? (
                <>
                  <p className="qualify-req-label">Requirement {currentStepNum}</p>
                  <h2 className="qualify-question">{question.label}</h2>
                  {question.detail && (
                    <p className="qualify-req-detail">{question.detail}</p>
                  )}
                </>
              ) : (
                <h2 className="qualify-question">{t(question.textKey)}</h2>
              )}
              <div className="qualify-actions">
                <button type="button" className="qualify-option" onClick={() => goTo(question.onYes, 'yes')}>
                  <span className="qualify-option-left">
                    <span className="qualify-option-icon">✓</span>
                    {isProcedure ? 'Yes, I meet this' : t('qualify.yes')}
                  </span>
                  <span className="qualify-arrow">→</span>
                </button>
                <button type="button" className="qualify-option" onClick={() => goTo(question.onNo, 'no')}>
                  <span className="qualify-option-left">
                    <span className="qualify-option-icon">×</span>
                    {isProcedure ? "Not yet" : t('qualify.no')}
                  </span>
                  <span className="qualify-arrow">→</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="qualify-question">
                <Trans i18nKey="qualify.altTitle" components={{ accent: <span /> }} />
              </h2>
              <p className="qualify-sub">{t('qualify.altDesc')}</p>
              <button type="button" className="qualify-continue" onClick={() => navigate(paths.signup)}>
                {t('qualify.continue')}
              </button>
            </>
          )}
        </section>

        {history.length > 0 && (
          <button type="button" className="qualify-start-over" onClick={startOver}>
            <i className="ri-refresh-line" aria-hidden />
            {t('qualify.startOver')}
          </button>
        )}

        {!isAlternatives && (
          <p className="qualify-privacy">
            <i className="ri-shield-check-line qualify-privacy-icon" aria-hidden />
            <span>
              <Trans
                i18nKey="qualify.qualifiedPrivacy"
                components={{
                  privacy: (
                    <button
                      type="button"
                      className="qualify-privacy-link"
                      onClick={() => setPrivacyOpen(true)}
                    />
                  ),
                }}
              />
            </span>
          </p>
        )}
      </main>

      <LegalModal type="privacy" open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </div>
  );
}
